import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPageAuth } from '../../../utils/page-auth';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import Notification from '../../../components/Notification/Notification';
import config from '../../../config/config';
import ApiClient from '../../../redux/models/src/ApiClient';
import cookies from 'react-cookies';
import PaginationOptions, { Page } from '../../../api/ui/paginationOptions';
import Visible from '../../../components/Custom/Visible';
import PaginationComponent from '../../../components/Custom/PaginationComponent';
import {
  SELECT_FORM_STYLE,
  STORAGE_TYPE,
} from '../../../constants/base';
import { uploadedImageListRequest } from '../../../redux/actions/app/devImageUploadActions';
import useTranslation from '../../../hooks/useTranslation';
import useSchemaImageUpload from '../../../schema/table-schema/useSchemaImageUpload';
import BOTable from '../../../components/Tables/BOTable';

// 파일 업로드(FileUpload)로 합쳐짐
// app/containers/AppManagement/FileUpload/FileUpload.js
function DevImageUpload() {
  // -- component const --//
  // 페이지 제목
  const title = 'admin.app.image.upload.title'; // '개발용 미디어 파일 업로드';
  const OPTION_KEY = Page.APP_DEV_IMAGE_UPLOAD;

  const { t } = useTranslation();
  const { schema } = useSchemaImageUpload();

  const allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'video/mp4',
    'video/webm',
    'video/mov',
  ];

  const apiURL = config.API_URL;
  const apiClient = new ApiClient();
  const headerData = apiClient.defaultHeaders;
  const accessToken = cookies.load('token');

  // -- useSelector & dispatch --//
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authReducer.user);

  // -- state --//
  const [readOnly, setReadOnly] = useState(true);

  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);

  const [storageType, setStorageTypes] = useState('');
  const [folderName, setFolderName] = useState('');
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [fileName, setFileName] = useState('');

  const [list, setList] = useState();
  const [totalCount, setTotalCount] = useState(0);

  // 페이지 옵션
  const [pageOption, setPageOption] = useState();
  const [page, setPage] = useState(1);
  const [pageLength, setPageLength] = useState(25);

  // 로딩
  const [isLoading, setIsLoading] = useState(false);

  // 이미지 || 비디오 상태
  const [fileKind, setFileKind] = useState('');

  // -- func & handler --//
  const fetchList = () => {
    setIsLoading(true);

    dispatch(
      uploadedImageListRequest({
        page: page,
        pageLength: pageLength,
        callback: (data) => {
          console.log(data);
          setIsLoading(false);
          setList(data.list);
          setTotalCount(data.totalCount);
        },
        error: (e) => {
          console.log(e);
          setIsLoading(false);
          setNotificationMessage(e.toString());
          setShowNotification(true);
        },
      }),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let missingInfo = '';

    if (!storageType || !folderName || !image) {
      missingInfo = !storageType
        ? '스토리지를 선택 하세요.'
        : !folderName
        ? '폴더명을 입력 하세요.'
        : '이미지를 업로드 해주세요.';
      setNotificationMessage(missingInfo);
      setShowNotification(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('Container', Number(storageType));
      formData.append('FolderName', folderName);
      formData.append('File', image);
      formData.append('FileName', fileName);

      for (const key of formData.keys()) {
        console.log(key, ':', formData.get(key));
      }

      const response = await axios.post(
        `${apiURL}api/System/UploadFile`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            AccessToken: accessToken,
            'CF-Access-Client-Id': headerData['CF-Access-Client-Id'],
            'CF-Access-Client-Secret': headerData['CF-Access-Client-Secret'],
          },
        },
      );

      console.log('Response:', response);
      setNotificationMessage('이미지 등록이 완료되었습니다.');
      setShowNotification(true);

      // 리스트 갱신
      fetchList();

      // 등록 조건 초기화
      setStorageTypes(null);
      setFolderName('');
      setFileName('');
      setImage(null);
      document.getElementById('file-input').value = null; // input 창의 파일 명 미리보기까지 초기화
      setPreviewUrl('');
    } catch (error) {
      console.error('Error:', error);
      setNotificationMessage(error.toString());
      setShowNotification(true);
    }
  };

  const handlePageChange = (_, value) => {
    setPage(Number(value));
  };

  const handlePageLengthChange = (value) => {
    setPageLength(value);
    setPage(1);
  };

  // -- react hooks --//
  useEffect(() => {
    getUserPageAuth(user).then((res) => setReadOnly(res));
  }, [user]);

  useEffect(() => {
    fetchList();
  }, [page, pageLength]);

  useEffect(() => {
    PaginationOptions.getInstance().then((instance) => {
      const option = instance?.getData(OPTION_KEY);
      setPageOption(option);

      if (option.list_count_elements?.length > 0) {
        setPageLength(Number(25));
      }
    });
  }, [OPTION_KEY]);

  return (
    <div className="page">
      <Helmet>
        <title>{t(title)}</title>
      </Helmet>

      <div className="topSection">
        <div className="leftSection">
          <p className="titleText">{t(title)}</p>
        </div>
      </div>

      <div className="spaceDiv10" />

      <div className="contentsArea paddingAll">
        <div className="rowDiv">
          <div className="descDiv date">
            <select
              className=""
              value={storageType ? storageType : ''}
              onChange={(e) => {
                setStorageTypes(e.target.value);
              }}
              style={SELECT_FORM_STYLE}
            >
              <option value="" disabled>
                스토리지
              </option>
              {STORAGE_TYPE.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.value}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={folderName}
              placeholder="업로드 폴더 이름"
              onChange={(e) => {
                setFolderName(e.target.value.trim());
              }}
            />

            <input
              id="file-input"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];

                if (allowedTypes.includes(file?.type) === false) {
                  setImage(null);
                  document.getElementById('file-input').value = null;
                  setPreviewUrl('');
                  setNotificationMessage('파일 확장자를 확인해주세요.');
                  setShowNotification(true);
                  return;
                }

                file?.type.includes('video')
                  ? setFileKind('video')
                  : setFileKind('image');

                if (file && file.size <= 1024 * 1024 * 30) {
                  // 파일 용량이 30MB 이하인 경우에만 업로드
                  setImage(file);
                  const reader = new FileReader();

                  console.log('file: ', file);
                  console.log('reader: ', reader);

                  reader.onloadend = () => {
                    setPreviewUrl(`${reader.result}`);
                  };
                  reader.readAsDataURL(file);

                  // 파일 선택 시 파일 이름 입력 필드에 확장자 추가
                  if (fileName) {
                    const fileExtension = file.name.split('.').pop();
                    setFileName(`${fileName}.${fileExtension}`);
                  }

                  return reader;
                } else {
                  // 파일 용량 초과 시에는 경고 메시지 등을 표시할 수 있습니다.
                  setNotificationMessage('파일 크기는 30MB 이하여야 합니다.');
                  setShowNotification(true);
                }
              }}
            />

            <input
              type="text"
              value={fileName}
              placeholder="파일 이름(확장자 필수)"
              onChange={(e) => {
                setFileName(e.target.value);
              }}
            />

            <button
              className="defaultBtn"
              onClick={handleSubmit}
              disabled={readOnly}
            >
              미디어 파일 등록
            </button>
          </div>
        </div>

        <div className="spaceDiv10" />

        <div className="rowDiv flex">
          <Visible visible={previewUrl.length > 0}>
            <Visible visible={fileKind === 'image'}>
              <img
                alt="업로드 할 이미지"
                src={previewUrl}
                style={{
                  width: 200,
                }}
              />
            </Visible>

            <Visible visible={fileKind === 'video'}>
              <video
                src={previewUrl}
                autoPlay
                loop
                style={{
                  width: 200,
                }}
              />
            </Visible>
          </Visible>
        </div>
      </div>

      <div className="spaceDiv10" />

      <Visible Visible visible={pageOption?.top_paging}>
        <PaginationComponent
          disable={false}
          option={pageOption}
          page={page}
          pageLength={pageLength}
          totalCount={totalCount}
          onChange={handlePageChange}
          onSelect={handlePageLengthChange}
        />
      </Visible>

      <div className="spaceDiv10" />

      <BOTable
        className={'image-upload-table'}
        schema={schema}
        rows={list}
        loading={isLoading}
        onClick={null}
      />

      <div className="spaceDiv10" />

      <Visible Visible visible={pageOption?.bottom_paging}>
        <PaginationComponent
          disable={false}
          option={pageOption}
          page={page}
          pageLength={pageLength}
          totalCount={totalCount}
          onChange={handlePageChange}
          onSelect={handlePageLengthChange}
        />
      </Visible>

      {showNotification ? (
        <Notification
          close={() => setShowNotification(false)}
          message={notificationMessage}
        />
      ) : null}
    </div>
  );
}

export default DevImageUpload;
